// src/firebase/AuthContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  getAuth,
  onIdTokenChanged,
  setPersistence,
  browserLocalPersistence,
  getRedirectResult,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "./firebaseConfig";
import { getSharedIdToken } from "../api/authToken";

function hasCachedFirebaseUser() {
  try {
    if (typeof window === "undefined") return false;
    return Object.keys(window.localStorage || {}).some((k) =>
      k.startsWith("firebase:authUser:")
    );
  } catch {
    return false;
  }
}

// Helper: classify permission/App Check errors
const isPermDenied = (e) =>
  (e && (e.code === "permission-denied" || e.code === "failed-precondition")) ||
  false;

const DEFAULT_USER_DOC = (email, displayName = "") => ({
  email: email || null,
  firstName: "",
  lastName: "",
  displayName: displayName || "",
  dateOfBirth: null,
  profileCompleted: false,
  emailVerified: false,
  authProviders: [],
  role: "observer", // admin | premium | observer (observer = PWG Free)
  status: "pending", // pending | active | suspended
  features: {
    pwg: false,
    teg: false,
    export: false,
    nba: false,
    mlb: false,
    wnba: false,
    soccer: false,
    nhl: false,
    nfl: false,
    cfb: false,
    mma: false,
  },
  onboarding: {
    welcomeEmailStatus: "pending",
    welcomeEmailSentAt: null,
    profileCompletedAt: null,
  },
  firstLoginAt: serverTimestamp(),
  lastLoginAt: serverTimestamp(),
  loginCount: 1,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

const AuthContext = createContext({
  user: null,
  loading: true,
  authloading: true,
  authInitialized: false,

  // NEW:
  userDoc: null,
  userDocLoading: false,

  subscription: null,
  isSubscribed: false,
  firestoreBlocked: false,
});

const db = getFirestore(app);
const functions = getFunctions(app, "us-central1");
const activatePWGUser = httpsCallable(functions, "activatePWGUser");
let firebaseBootstrapPromise;
let firebaseRedirectPromise;

function initializeFirebaseAuth(auth) {
  if (!firebaseBootstrapPromise) {
    firebaseBootstrapPromise = setPersistence(auth, browserLocalPersistence)
      .catch((e) => {
        console.warn("AuthContext: Firebase bootstrap:", e?.code || e);
        return null;
      });
  }
  return firebaseBootstrapPromise;
}

function finishFirebaseRedirect(auth) {
  if (!firebaseRedirectPromise) {
    firebaseRedirectPromise = getRedirectResult(auth).catch((e) => {
      console.warn("AuthContext: Firebase redirect result:", e?.code || e);
      return null;
    });
  }
  return firebaseRedirectPromise;
}

// Create users/<uid> on first sign-in and maintain only client-writable profile
// metadata afterward. Role, status, features, and subscription are deliberately
// excluded from existing-user patches because Firestore reserves those fields
// for the backend/admin lifecycle.
async function ensureUserDoc(uid, email, displayName = "") {
  if (!uid) return { ok: false, reason: "no-uid" };

  const ref = doc(db, "users", uid);

  try {
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      const initialData = DEFAULT_USER_DOC(email, displayName);
      await setDoc(ref, initialData, { merge: true });
      return { ok: true, ref, initialData };
    } else {
      // Backfill only fields the signed-in user is permitted to maintain.
      const data = snap.data() || {};
      const patch = {
        updatedAt: serverTimestamp(),
      };

      if (email && data.email !== email) patch.email = email;
      if (!("firstName" in data)) patch.firstName = "";
      if (!("lastName" in data)) patch.lastName = "";
      if (!("displayName" in data)) patch.displayName = displayName || "";
      if (!("dateOfBirth" in data)) patch.dateOfBirth = null;
      if (!("profileCompleted" in data)) patch.profileCompleted = false;
      if (!("emailVerified" in data)) patch.emailVerified = false;
      if (!("authProviders" in data)) patch.authProviders = [];
      if (!("firstLoginAt" in data)) patch.firstLoginAt = serverTimestamp();
      patch.lastLoginAt = serverTimestamp();
      if (!("loginCount" in data)) patch.loginCount = 1;
      if (!("onboarding" in data)) {
        patch.onboarding = {
          welcomeEmailStatus: "pending",
          welcomeEmailSentAt: null,
          profileCompletedAt: null,
        };
      }
      // Existing-user maintenance is deliberately outside the authorization
      // critical path. The listener below will observe it when it completes.
      setDoc(ref, patch, { merge: true }).catch((error) => {
        console.warn("AuthContext: profile maintenance skipped", error?.code || error);
      });
      return { ok: true, ref, initialData: data };
    }
  } catch (e) {
    if (isPermDenied(e)) {
      console.warn("ensureUserDoc blocked by Firestore permissions/AppCheck", {
        code: e.code,
        message: e.message,
        uid,
      });
      return { ok: false, ref, err: e, reason: "perms" };
    }
    console.error("ensureUserDoc error:", e);
    return { ok: false, ref, err: e, reason: "other" };
  }
}

export const AuthProvider = ({ children }) => {
  const auth = getAuth(app);

  const [user, setUser] = useState(null);

  // This is your auth loading (token/user)
  const [authloading, setAuthLoading] = useState(hasCachedFirebaseUser());
  const [authInitialized, setAuthInitialized] = useState(false);

  // NEW: full Firestore user doc + loading
  const [userDoc, setUserDoc] = useState(null);
  const [userDocLoading, setUserDocLoading] = useState(false);

  // subscription state from Firestore doc (if present)
  const [subscription, setSubscription] = useState(null);

  // Firestore blocked?
  const [firestoreBlocked, setFirestoreBlocked] = useState(false);
  const activeUserIdRef = useRef("");

  useEffect(() => {
    let unsubAuth = () => {};
    let unsubUserDoc = () => {};

    (async () => {
      try {
        await initializeFirebaseAuth(auth);

        unsubAuth = onIdTokenChanged(auth, async (fbUser) => {
          try {
            if (!fbUser) {
              try { unsubUserDoc(); } catch {}
              activeUserIdRef.current = "";
              setUser(null);
              setUserDoc(null);
              setUserDocLoading(false);
              setSubscription(null);
              setFirestoreBlocked(false);
              return;
            }

            // Auth user object for app usage
            const sameUser = activeUserIdRef.current === fbUser.uid;
            const token = await getSharedIdToken();
            const u = {
              uid: fbUser.uid,
              email: fbUser.email,
              displayName: fbUser.displayName,
              photoURL: fbUser.photoURL,
              emailVerified: fbUser.emailVerified === true,
              providerIds: (fbUser.providerData || []).map((p) => p.providerId),
              token,
              _fb: fbUser,
            };
            setUser(u);

            // Token refreshes must update credentials without tearing down the
            // profile listener or putting an already-authorized shell back into
            // its initial loading state.
            if (sameUser) return;
            activeUserIdRef.current = fbUser.uid;
            try { unsubUserDoc(); } catch {}

            // Begin loading Firestore userDoc
            setUserDocLoading(true);

            const res = await ensureUserDoc(u.uid, u.email, u.displayName || "");

            if (res.ok && res.initialData) {
              setUserDoc(res.initialData);
              setSubscription(res.initialData?.subscription || null);
              setUserDocLoading(false);
            }

            // Mirror Firebase Auth verification/provider state into Firestore so the
            // admin console can display it. Backend activation still verifies Auth
            // directly and does not trust this client-written field.
            if (res.ok && res.ref) {
              setDoc(res.ref, {
                emailVerified: u.emailVerified,
                authProviders: u.providerIds,
                updatedAt: serverTimestamp(),
              }, { merge: true }).catch((error) => {
                console.warn("AuthContext: auth profile mirror skipped", error?.code || error);
              });
            }

            // Count one login per browser session, not every token refresh.
            try {
              const loginKey = `pwg:login-counted:${u.uid}`;
              if (!window.sessionStorage.getItem(loginKey) && res.ok && res.ref) {
                setDoc(res.ref, {
                  lastLoginAt: serverTimestamp(),
                  loginCount: increment(1),
                  updatedAt: serverTimestamp(),
                }, { merge: true }).catch((error) => {
                  console.warn("AuthContext: login tracking skipped", error?.code || error);
                });
                window.sessionStorage.setItem(loginKey, "1");
              }
            } catch (loginErr) {
              console.warn("AuthContext: login tracking skipped", loginErr);
            }

            if (res.ok && res.ref) {
              setFirestoreBlocked(false);

              unsubUserDoc = onSnapshot(
                res.ref,
                (snap) => {
                  const data = snap.data() || null;
                  setUserDoc(data);
                  setSubscription(data?.subscription || null);
                  setUserDocLoading(false);
                },
                (err) => {
                  console.error("AuthContext: user doc listener error:", err);
                  setUserDoc(null);
                  setSubscription(null);
                  setUserDocLoading(false);
                  if (isPermDenied(err)) setFirestoreBlocked(true);
                }
              );
            } else {
              // Can't read/write Firestore (rules/AppCheck/emulator mismatch)
              setUserDoc(null);
              setSubscription(null);
              setUserDocLoading(false);
              setFirestoreBlocked(res.reason === "perms");
            }
          } finally {
            setAuthLoading(false);
            setAuthInitialized(true);
          }
        });

        // Redirect cleanup must never delay registration of the auth listener.
        // Some browser/session combinations leave getRedirectResult pending even
        // though the cached Firebase user is already ready to authorize.
        finishFirebaseRedirect(auth);
      } catch (e) {
        console.error("AuthContext init error:", e?.code || e);
        setAuthLoading(false);
        setAuthInitialized(true);
        setFirestoreBlocked(isPermDenied(e));
      }
    })();

    return () => {
      try {
        unsubAuth();
      } catch {}
      try {
        unsubUserDoc();
      } catch {}
    };
  }, [auth]);

  const updateProfile = async ({ firstName, lastName, dateOfBirth = null }) => {
    if (!user?.uid) throw new Error("No signed-in user.");

    const cleanFirst = String(firstName || "").trim();
    const cleanLast = String(lastName || "").trim();
    if (!cleanFirst || !cleanLast) {
      throw new Error("First name and last name are required.");
    }

    const ref = doc(db, "users", user.uid);
    await setDoc(ref, {
      firstName: cleanFirst,
      lastName: cleanLast,
      displayName: `${cleanFirst} ${cleanLast}`.trim(),
      dateOfBirth: dateOfBirth || null,
      profileCompleted: true,
      onboarding: {
        profileCompletedAt: serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
    }, { merge: true });

    // Directly invoke the secure backend activation function. This avoids
    // relying on an Eventarc document-update trigger for onboarding.
    try {
      const result = await activatePWGUser();
      return result?.data || { activated: true };
    }  catch (error) {
        console.error("====================================");
        console.error("activatePWGUser FAILED");
        console.error("code:", error.code);
        console.error("message:", error.message);
        console.error("details:", error.details);
        console.error("full error:", error);
        console.error("====================================");

        throw error;
      }
  };

  // Derived boolean: active or trialing
  const isSubscribed =
    !!subscription &&
    ["active", "trialing"].includes(
      String(subscription?.status || "").toLowerCase()
    );

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: authloading,
        authloading,
        authInitialized,

        // NEW:
        userDoc,
        userDocLoading,

        subscription,
        isSubscribed,
        firestoreBlocked,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
